/**
 * Country Service
 * 
 * API calls for country management.
 * All endpoints require SUPER_ADMIN role.
 */

import { apiClient } from './api.service';
import type {
  Country,
  CountryQueryParams,
  CountryPaginatedResponse,
  CreateCountryDto,
  UpdateCountryDto,
  ActivationRequirementsResponse,
} from '@/types/country.types';

// ============================================================================
// COUNTRY SERVICE
// ============================================================================

class CountryService {
  private readonly basePath = '/countries';

  /**
   * Get paginated list of countries
   */
  async getAll(
    params?: CountryQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<CountryPaginatedResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const endpoint = query ? `${this.basePath}?${query}` : this.basePath;

    const response = await apiClient.get<CountryPaginatedResponse>(endpoint, {
      signal: opts.signal,
    });
    return response.data;
  }

  /**
   * Get country by ID
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Country> {
    const response = await apiClient.get<Country>(`${this.basePath}/${id}`, {
      signal: opts.signal,
    });
    return response.data;
  }

  /**
   * Get country by code
   */
  async getByCode(
    code: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Country> {
    const response = await apiClient.get<Country>(
      `${this.basePath}/code/${code}`,
      { signal: opts.signal },
    );
    return response.data;
  }

  /**
   * Create new country
   */
  async create(data: CreateCountryDto): Promise<Country> {
    const response = await apiClient.post<Country>(this.basePath, data);
    return response.data;
  }

  /**
   * Update country
   */
  async update(id: string, data: UpdateCountryDto): Promise<Country> {
    const response = await apiClient.patch<Country>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Activate country
   */
  async activate(id: string): Promise<Country> {
    const response = await apiClient.post<Country>(`${this.basePath}/${id}/activate`);
    return response.data;
  }

  /**
   * Deactivate country
   */
  /**
   * Pide el código que hace falta para desactivar un país.
   *
   * Va atado a esa operación: no sirve para entrar ni para ninguna otra cosa.
   */
  async requestDeactivateCode(id: string): Promise<{ expiresIn: number }> {
    const response = await apiClient.post<{ expiresIn: number }>(
      `${this.basePath}/${id}/deactivate/request-code`,
    );
    return response.data;
  }

  /**
   * Apaga un país. Exige el código del correo **y** el del autenticador:
   * detiene su operación entera y es la única forma de llegar ahí.
   */
  async deactivate(
    id: string,
    codes: { emailCode: string; totpCode: string },
  ): Promise<Country> {
    const response = await apiClient.post<Country>(
      `${this.basePath}/${id}/deactivate`,
      codes,
    );
    return response.data;
  }

  /**
   * Check activation requirements
   */
  async checkActivationRequirements(id: string): Promise<ActivationRequirementsResponse> {
    const response = await apiClient.get<ActivationRequirementsResponse>(
      `${this.basePath}/${id}/activation-requirements`
    );
    return response.data;
  }

  /**
   * Delete country
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const countryService = new CountryService();
